/**
 * 联络站一级页（05 开发文档 LetterDesk + 定稿原型 contact.html/contact.js 逐行移植）。
 * 两封信笺（微倾斜、信封主视觉）；点击 → 信纸抽出转场 0.9s（抽出 0.55s + 渐隐 0.3s）
 * → 二级页 ?from=letter；reduced-motion 直接跳转；动画中防重复点击（定稿动效不得简化）。
 */
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useLocale } from '../lib/locale';
import { SiteHeader } from '../components/SiteHeader';

const LEAVE_MS = 900; // 抽出 0.55s + 淡出 0.3s（delay 0.35s）≈ 0.9s（原型定稿）

export function ContactPage() {
  const { locale } = useLocale();
  const navigate = useNavigate();
  const [leaving, setLeaving] = useState<'creators' | 'partners' | null>(null);

  const openLetter = (scene: 'creators' | 'partners') => {
    if (leaving) return; // 动画中防重复点击（原型 is-leaving 守卫）
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      navigate(`/${locale}/contact/${scene}?from=letter`);
      return;
    }
    setLeaving(scene);
    window.setTimeout(() => {
      navigate(`/${locale}/contact/${scene}?from=letter`);
    }, LEAVE_MS);
  };

  return (
    <main className='page contact-page'>
      <SiteHeader />
      <header className='contact-hero' data-page-intro>
        <p className='eyebrow'>Contact Station / 联络站</p>
        <h1>{locale === 'zh-CN' ? '向小镇递一封信' : 'Send a note to the town'}</h1>
        <p className='contact-lede'>
          {locale === 'zh-CN'
            ? '选一张信笺，镇上的信使会替你把话带到。'
            : 'Pick a letterhead — the town messenger will carry your words.'}
        </p>
      </header>
      <section className='letters' aria-label={locale === 'zh-CN' ? '联络入口' : 'Contact entries'}>
        {/* 信笺一：创作者。整卡即链接；信封占满卡面（原型 envelope-scale 结构原样）。 */}
        <button
          type='button'
          className={`letter letter-creators${leaving === 'creators' ? ' is-leaving' : ''}`}
          onClick={() => openLetter('creators')}
        >
          <span className='envelope-scale' aria-hidden='true'>
            <span className='envelope'>
              <span className='letter-paper'>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <b>For Creators</b>
              </span>
              <span className='envelope-back'></span>
              <span className='envelope-flap'></span>
            </span>
          </span>
          <span className='letter-overlay'>
            <strong className='letter-title'>{locale === 'zh-CN' ? '我是创作者' : "I'm a creator"}</strong>
            <span className='letter-cta'>
              <span className='letter-cta-arrow' aria-hidden='true'>→</span>
              <span className='visually-hidden'>
                {locale === 'zh-CN' ? '展开信笺，进入创作者联络' : 'Open the letter — contact for creators'}
              </span>
            </span>
          </span>
        </button>
        {/* 信笺二：合作伙伴（与信笺一微相对倾斜，原型同款） */}
        <button
          type='button'
          className={`letter letter-partners${leaving === 'partners' ? ' is-leaving' : ''}`}
          onClick={() => openLetter('partners')}
        >
          <span className='envelope-scale' aria-hidden='true'>
            <span className='envelope'>
              <span className='letter-paper'>
                <i></i>
                <i></i>
                <i></i>
                <i></i>
                <b>For Partners</b>
              </span>
              <span className='envelope-back'></span>
              <span className='envelope-flap'></span>
            </span>
          </span>
          <span className='letter-overlay'>
            <strong className='letter-title'>{locale === 'zh-CN' ? '我是合作伙伴' : 'I\'m a partner'}</strong>
            <span className='letter-cta'>
              <span className='letter-cta-arrow' aria-hidden='true'>→</span>
              <span className='visually-hidden'>
                {locale === 'zh-CN' ? '展开信笺，进入合作伙伴联络' : 'Open the letter — contact for partners'}
              </span>
            </span>
          </span>
        </button>
      </section>
      <p className='contact-expect'>
        {locale === 'zh-CN'
          ? '信使会在两个工作日内把信送达，回复通常需要再等上几天。'
          : 'The messenger delivers within two working days; a reply may take a few more.'}
      </p>
    </main>
  );
}
